import api from './api';
import { isPublicAttachmentUrl } from './attachmentService';
import type { Bid, CreateBidRequest } from '../types';

function sanitizeBidData(data: CreateBidRequest): CreateBidRequest {
  const raw = data as CreateBidRequest & { attachmentUrl?: string; attachmentName?: string };
  const { attachmentUrl, attachments, ...rest } = raw;
  const safeAttachments = Array.isArray(attachments)
    ? attachments.filter((a) => a?.fileUrl && isPublicAttachmentUrl(a.fileUrl))
    : undefined;
  if (attachmentUrl && !isPublicAttachmentUrl(attachmentUrl)) {
    return { ...(rest as CreateBidRequest), ...(safeAttachments ? { attachments: safeAttachments } : {}) };
  }
  return {
    ...data,
    ...(safeAttachments ? { attachments: safeAttachments } : {}),
  };
}

function normalizeBid(bid: Bid): Bid {
  return {
    ...bid,
    startTime:
      typeof bid.startTime === 'string' ? bid.startTime : new Date(bid.startTime).toISOString(),
    endTime: typeof bid.endTime === 'string' ? bid.endTime : new Date(bid.endTime).toISOString(),
    history: (bid.history ?? []).map((h) => ({
      ...h,
      id: h.id ?? `hist-${h.vendorName}-${h.amount}-${h.timestamp}`,
      timestamp:
        typeof h.timestamp === 'string' ? h.timestamp : new Date(h.timestamp).toISOString(),
    })),
  };
}

export const bidService = {
  /** `scope=created` — auctions the current user created (any status). Default: created or invited. */
  getBids: async (scope?: 'created'): Promise<Bid[]> => {
    const q = scope === 'created' ? '?scope=created' : '';
    const response = await api.get<Bid[]>(`/api/bids${q}`);
    if (response.success && response.data) return response.data.map(normalizeBid);
    throw new Error(response.error || 'Failed to fetch bids');
  },

  resendEndedBidReport: async (bidId: string): Promise<void> => {
    const response = await api.post<{ message?: string }>(`/api/bids/${bidId}/resend-ended-report`);
    if (!response.success) {
      throw new Error(response.error || 'Failed to resend report');
    }
  },

  getBid: async (id: string): Promise<Bid> => {
    const response = await api.get<Bid>(`/api/bids/${id}`);
    if (response.success && response.data) return normalizeBid(response.data);
    throw new Error(response.error || 'Failed to fetch bid');
  },

  updateBid: async (bidId: string, bidData: CreateBidRequest): Promise<{ id: string }> => {
    const response = await api.put<{ id: string }>(
      `/api/bids/${bidId}`,
      sanitizeBidData(bidData)
    );
    if (response.success && response.data) return response.data;
    throw new Error(response.error || 'Failed to update bid');
  },

  /** POST /api/bids returns `{ id }` only — must not run `normalizeBid` (missing dates throw Invalid time value). */
  createBid: async (bidData: CreateBidRequest): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/api/bids', sanitizeBidData(bidData));
    if (response.success && response.data) return response.data;
    throw new Error(response.error || 'Failed to create bid');
  },

  placeBid: async (bidId: string, amount: number): Promise<Bid> => {
    const response = await api.post<Bid>(`/api/bids/${bidId}/place-bid`, { amount });
    if (response.success && response.data) return normalizeBid(response.data);
    throw new Error(response.error || 'Failed to place bid');
  },
};
